import { debug } from "../../utils"
import * as Types from "./api.types"
import { Client } from "./client"

/**
 * Manages all requests to the API.
 */

export class APIService {
  client = new Client()
  baseUrl: string

  constructor(baseUrl: string) {
    debug(`new APIService ${baseUrl}`)
    this.baseUrl = baseUrl
  }

  url(path: string) {
    return `${this.baseUrl}${path}`
  }

  // 登录
  async login(data: Types.LoginParams): Promise<Types.AuthResult> {
    return await this.client.json<Types.AuthResult>(this.url(`/auth/login`), data)
  }

  // 注册
  async register(data: Types.RegisterParams): Promise<Types.AuthResult> {
    return await this.client.json<Types.AuthResult>(this.url(`/auth/register`), data)
  }

  // 手机验证码
  async phoneSmsv1(data: Types.PhoneCodeParams): Promise<Types.AuthResult> {
    return await this.client.json<Types.AuthResult>(this.url(`/user/sms/code/v1`), data)
  }

  // 登出
  async logout(data: Types.LogoutParams): Promise<Types.LogoutResult> {
    return await this.client.post(this.url(`/auth/logout`), data)
  }

  // 忘记密码
  async forgetPassword(data: Types.ForgetPasswordParams): Promise<any> {
    return await this.client.post<any>(this.url(`/auth/pwd_code`), data)
  }

  // 重置密码
  async resetPassword(data: Types.ResetPasswordParams): Promise<any> {
    return await this.client.post<any>(this.url(`/auth/reset/pwd`), data)
  }

  // 上传
  async upload(data: Types.UploadParams): Promise<Types.UploadResult> {
    return await this.client.upload(this.url("/upload/file"), data)
  }

  // 上传
  async uploadClient(data: Types.UploadClientParams): Promise<Types.UploadClientResult> {
    return await this.client.get<Types.UploadClientResult>(
      this.url(`/upload/client/payload?filename=${data.filename}`),
    )
  }

  // 確認token是否有效
  async ping(data: Types.PingParams): Promise<Types.PingResult> {
    return await this.client.get<Types.PingResult>(this.url(`/auth/ping`), {
      token: data.token
    })
  }
  

  async uploadToS3Storage(
    s3url: Types.UploadClientResult["url"],
    formData: Types.UploadToS3StorageParams,
    onProgressUpdate: (progress: number) => void,
  ): Promise<Types.UploadToS3StorageResult> {
    return await this.client.uploadToS3Storage<Types.UploadToS3StorageResult>(s3url, formData, onProgressUpdate)
  }

  // 下载
  async download(data: Types.DownloadParams) {
    return await this.client.download(data)
  }

  // 确认是否有新版本
  async checkVersion(data: Types.CheckVersionParams): Promise<Types.CheckVersionResult> {
    return await this.client.get<Types.CheckVersionResult>(
      this.url(`/app_versions/checkingVersion?version=${data.version}&device=${data.device}`),
    )
  }

  // 银行/配置列表
  async getBankList(): Promise<any> {
    return await this.client.get<any>(this.url(`/config/all`))
  }

  // 用户钱包资讯
  async getWalletInfo(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.getWithAuth<Types.GetResult>(this.url(`/user/info`), data.token)
  }

  // 钱包流水
  async getWalletLog(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.getWithAuth<Types.GetResult>(
      this.url(`/wallet/logs`),
      data.token,
      data.params,
    )
  }

  async getForceCs(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.getWithAuth<Types.GetResult>(
      this.url(`/config/cs`),
      data.token,
      data.params,
    )
  }

  async getTakeOrders(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.getWithAuth<Types.GetResult>(
      this.url(`/order/take_orders`),
      data.token,
      data.params,
    )
  }

  async getTakeOrdersSold(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.getWithAuth<Types.GetResult>(
      this.url(`/order/take_orders/sold`),
      data.token,
      data.params,
    )
  }

  async getTakeOrdersDisputed(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.getWithAuth<Types.GetResult>(
      this.url(`/order/take_orders/disputed`),
      data.token,
      data.params,
    )
  }

  async getPaymentList(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.getWithAuth<Types.GetResult>(
      this.url(`/user/payment/list`),
      data.token,
    )
  }

  async getPendingOrders(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.getWithAuth<Types.GetResult>(
      this.url(`/order/pending_orders`),
      data.token,
    )
  }

  async getMyOrders(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.getWithAuth<Types.GetResult>(
      this.url(`/order/my/sold?filters=${encodeURIComponent(JSON.stringify({ is_settled: data.isSettled }))}`),
      data.token,
    )
  }

  async getMyOrderDetail(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.getWithAuth<Types.GetResult>(
      this.url(`/order/pending_orders_details/${data.id}`),
      data.token,
    )
  }

  async deletePayment(data: Types.DeletePayParams): Promise<Types.GetResult> {
    return await this.client.deleteWithAuth<Types.GetResult>(
      this.url(`/user/payment/delete`),
      data.token,
      data,
    )
  }

  async verify(data: Types.VerifyParams): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/user/verify`),
      data.token,
      data,
    )
  }

  async phoneVerify(data: Types.PhoneSmsParams): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/user/phone/verify`),
      data.token,
      data,
    )
  }

  async phoneSms(data: Types.PhoneSmsParams): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/user/phone/sms/code`),
      data.token,
      data,
    )
  }

  async paypws(data: Types.CreatePayPwdParams): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/user/pay_pwd/create`),
      data.token,
      data,
    )
  }

  async payCheck(data: Types.CheckPayParams): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/user/pay_pwd/check`),
      data.token,
      data,
    )
  }

  async updatePaypws(data: Types.UpdatePayPwdParams): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/user/pay_pwd/update`),
      data.token,
      data,
    )
  }

  async createPay(data: Types.CreatePayParams, token: string): Promise<Types.PostResult> {
    return await this.client.createPay<Types.PostResult>(
      this.url(`/user/payment/create`),
      token,
      data,
    )
  }

  async getExchangeRate(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.getWithAuth<Types.GetResult>(this.url(`/exchange/conf`), data.token)
  }

  async getExchangeList(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.getWithAuth<Types.GetResult>(this.url(`/exchange/list`), data.token)
  }

  async cancelExchange(data: Types.CancelExchangeParams): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/exchange/cancel`),
      data.token,
      data,
    )
  }

  async createExchange(data: Types.CreateExchangeParams): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/exchange/create`),
      data.token,
      data,
    )
  }

  async getComplaintList(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.getWithAuth<Types.GetResult>(this.url(`/exchange/list`), data.token)
  }

  async saveComplaint(data: Types.SaveComplaintParams): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/exchange/create`),
      data.token,
      data,
    )
  }

  async getMyCredit(data: Types.MyCreditParams, token: string): Promise<Types.GetResult> {
    const dataStr = JSON.stringify(data)
    return await this.client.getWithAuth<Types.GetResult>(
      this.url(`/evaluation/list?filters=${encodeURIComponent(dataStr)}`),
      token,
    )
  }

  async getCreditRule(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.getWithAuth<Types.GetResult>(
      this.url(`/evaluation/get/credit_score_rule`),
      data.token,
    )
  }

  async getRatingRule(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.getWithAuth<Types.GetResult>(
      this.url(`/evaluation/get/order_rule`),
      data.token,
    )
  }

  async saveCredit(data: Types.SaveCreditParams, token: string): Promise<Types.PostResult> {
    return await this.client.postFormDataWithAuth<Types.PostResult>(
      this.url(`/evaluation/add`),
      token,
      data,
    )
  }

  async emailSms(data: Types.EmailSmsParams, token: string): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/user/send_email`),
      token,
      data,
    )
  }

  async updateEmail(data: Types.UpdateEmailParams, token: string): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/user/update_email`),
      token,
      data,
    )
  }

  async revisePassword(data: Types.RevisePasswordParams): Promise<any> {
    return await this.client.postWithAuth<any>(this.url(`/auth/new_reset/pwd`), data.token, data)
  }
}
